/**
 * Header Search with Shopify Predictive Search API
 */

// Utility functions (matching theme.js patterns)
const select = (selector, context = document) => context.querySelector(selector);
const selectAll = (selector, context = document) => Array.from(context.querySelectorAll(selector));

const addClass = (element, ...classes) => {
  if (element) element.classList.add(...classes);
};

const removeClass = (element, ...classes) => {
  if (element) element.classList.remove(...classes);
};

const toggleClass = (element, ...classes) => {
  if (element) {
    classes.forEach(className => element.classList.toggle(className));
  }
};

const hasClass = (element, className) => {
  return element ? element.classList.contains(className) : false;
};

/**
 * HeaderPredictiveSearch - Handles API calls to Shopify Predictive Search
 */
function HeaderPredictiveSearch(resultsContainer) {
  const cachedResults = {};

  function getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    if (cachedResults[queryKey]) {
      renderSearchResults(cachedResults[queryKey]);
      return;
    }

    fetchResults(searchTerm, queryKey);
  }

  function fetchResults(searchTerm, queryKey) {
    const params = new URLSearchParams();
    params.set("section_id", "header-predictive-search");
    params.set("q", searchTerm);

    // Include searchable fields if configured
    if (window.theme && window.theme.searchableFields) {
      params.set("resources[options][fields]", window.theme.searchableFields);
    }

    const url = `${window.theme.routes.predictive_search_url}?${params}`;

    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then(text => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-header-predictive-search").innerHTML;

        cachedResults[queryKey] = resultsMarkup;
        renderSearchResults(resultsMarkup);
      })
      .catch(error => {
        console.error('Error fetching search results:', error);
      });
  }

  function renderSearchResults(resultsMarkup) {
    resultsContainer.innerHTML = resultsMarkup;
  }

  function clearResults() {
    resultsContainer.innerHTML = "";
  }

  return {
    getSearchResults,
    clearResults
  };
}

/**
 * HeaderSearch - Main controller for header search UI
 */
function HeaderSearch(node) {
  if (!node) return;

  const form = select("[data-header-search-form]", node);
  const input = select("[data-header-search-input]", node);
  const clearButton = select("[data-header-search-clear]", node);
  const resultsContainer = select("[data-header-search-results]", node);
  const resultsWrapper = select("[data-header-search-results-wrapper]", node);

  const predictiveSearch = HeaderPredictiveSearch(resultsContainer);

  let searchTimeout;
  const SEARCH_DELAY = 300; // Debounce delay in milliseconds

  // Event listeners
  const events = [];

  // Input event - trigger search
  if (input) {
    events.push(
      addEventListener(input, "input", handleInput)
    );

    // Focus event - show results if they exist
    events.push(
      addEventListener(input, "focus", handleFocus)
    );
  }

  // Clear button click
  if (clearButton) {
    events.push(
      addEventListener(clearButton, "click", handleClear)
    );
  }

  // Click outside to close results
  events.push(
    addEventListener(document, "click", handleClickOutside)
  );

  // Form submit - allow default behavior to go to search page
  if (form) {
    events.push(
      addEventListener(form, "submit", handleSubmit)
    );
  }

  function handleInput(e) {
    const value = e.target.value.trim();

    // Clear timeout if user is still typing
    clearTimeout(searchTimeout);

    // Toggle clear button visibility
    if (value !== "") {
      addClass(clearButton, "header-search__clear--visible");
      addClass(input, "header-search__input--active");
      addClass(form, "header-search__form--active");

      // Debounce search - wait for user to stop typing
      searchTimeout = setTimeout(() => {
        predictiveSearch.getSearchResults(value);
        showResults();
      }, SEARCH_DELAY);
    } else {
      reset();
    }
  }

  function handleFocus() {
    if (input.value.trim() !== "" && resultsContainer.innerHTML !== "") {
      showResults();
    }
  }

  function handleClear(e) {
    e.preventDefault();
    reset();
    input.focus();
  }

  function handleClickOutside(e) {
    if (!node.contains(e.target)) {
      hideResults();
    }
  }

  function handleSubmit(e) {
    // Allow form to submit naturally to search page
    // Clear any pending searches
    clearTimeout(searchTimeout);
  }

  function reset() {
    input.value = "";
    removeClass(clearButton, "header-search__clear--visible");
    removeClass(input, "header-search__input--active");
    removeClass(form, "header-search__form--active");
    predictiveSearch.clearResults();
    hideResults();
    clearTimeout(searchTimeout);
  }

  function showResults() {
    if (resultsContainer.innerHTML.trim() !== "") {
      addClass(resultsWrapper, "header-search__results-wrapper--visible");
      addClass(node, "header-search--active");
    }
  }

  function hideResults() {
    removeClass(resultsWrapper, "header-search__results-wrapper--visible");
    removeClass(node, "header-search--active");
  }

  // Helper function to add event listeners and track them
  function addEventListener(element, event, handler) {
    element.addEventListener(event, handler);
    return () => element.removeEventListener(event, handler);
  }

  // Cleanup function
  function destroy() {
    events.forEach(unsubscribe => unsubscribe());
    clearTimeout(searchTimeout);
  }

  return {
    destroy
  };
}

/**
 * Initialize header search on DOM ready
 */
function initHeaderSearch() {
  const headerSearchNodes = selectAll("[data-header-search]");

  headerSearchNodes.forEach(node => {
    HeaderSearch(node);
  });
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHeaderSearch);
} else {
  initHeaderSearch();
}

// Also initialize on Shopify section load (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener("shopify:section:load", initHeaderSearch);
}

// "Please select" placeholder — override theme's default "Unavailable" text
(function() {
  function init() {
    var optionSelects = document.querySelectorAll('[data-product-option] select');
    var addToCartBtns = document.querySelectorAll('[data-add-to-cart]');
    var stickyChangeBtn = document.querySelector('[data-change-option-trigger]');
    var stickyOptions = document.querySelector('.sticky-atc-bar__meta-options');

    if (!optionSelects.length || !addToCartBtns.length) return;

    function hasUnselectedOption() {
      for (var i = 0; i < optionSelects.length; i++) {
        if (optionSelects[i].value === '') return true;
      }
      return false;
    }

    function applyPlaceholderState() {
      if (hasUnselectedOption()) {
        addToCartBtns.forEach(function(btn) {
          btn.setAttribute('disabled', 'disabled');
          var text = btn.querySelector('[data-add-to-cart-text]');
          if (text) text.textContent = 'Please make a selection';
        });
        if (stickyChangeBtn) stickyChangeBtn.textContent = 'Please select';
        if (stickyOptions) stickyOptions.textContent = '';
      } else {
        if (stickyChangeBtn) stickyChangeBtn.textContent = stickyChangeBtn.dataset.langChange || 'Change';
        if (stickyOptions) {
          var selected = [];
          optionSelects.forEach(function(sel) { selected.push(sel.value); });
          stickyOptions.textContent = selected.join(', ');
        }
      }
    }

    // Override on page load (runs after theme's init sets "Unavailable")
    requestAnimationFrame(applyPlaceholderState);

    // Also override after theme's change handler fires
    optionSelects.forEach(function(sel) {
      sel.addEventListener('change', function() {
        setTimeout(applyPlaceholderState, 0);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

document.addEventListener('DOMContentLoaded', function () {
	const productJsonElement = document.querySelector('script[data-product-json]');
	if (!productJsonElement) return;

	try {
		const productData = JSON.parse(productJsonElement.textContent);

		productData.options.forEach((optionName, index) => {
			const select = document.getElementById(`option${index + 1}`);
			if (!select) return;

			const unavailableValues = new Set();

			productData.variants.forEach(variant => {
				if (!variant.available) {
					const value = variant.options[index];
					unavailableValues.add(value);
				}
			});

			[...select.options].forEach(opt => {
				const label = opt.textContent.trim();
				if (unavailableValues.has(opt.value) && !label.includes('Unavailable')) {
					opt.textContent = `${label} (Out of stock)`;
				}
			});
		});
	} catch (err) {
		console.error('Invalid product JSON:', err);
	}
});

/**
 * Collection Read More Smooth Scroll
 * Handles smooth scrolling to the collection read more section with proper offset calculations
 */
function initCollectionReadMoreScroll() {
	// Listen for clicks on links within the collection banner text
	document.addEventListener('click', function(e) {
		// Check if the click is on a link element
		const link = e.target.closest('a');
		if (!link) return;

		// Check if the link is within the collection banner text div
		const bannerText = document.getElementById('collection-banner-text');
		if (!bannerText || !bannerText.contains(link)) return;

		// Check if the link text is "Read More" or "Read more" (case-insensitive)
		const linkText = link.textContent.trim().toLowerCase();
		if (linkText !== 'read more') return;

		// Check if the read more section exists
		const readMoreSection = document.getElementById('collection-read-more');
		if (!readMoreSection) return;

		// Prevent default link behavior and stop propagation
		e.preventDefault();
		e.stopPropagation();

		// Calculate offsets for sticky elements
		let totalOffset = 0;

		// Check for sticky announcement bar
		const stickyAnnouncement = document.querySelector('.announcement-bar[data-enable-sticky-announcement-bar]');
		if (stickyAnnouncement) {
			const announcementSetting = stickyAnnouncement.getAttribute('data-enable-sticky-announcement-bar');
			const isMobile = window.matchMedia('(max-width: 959px)').matches;

			if ((isMobile && announcementSetting.includes('mobile')) ||
			    (!isMobile && announcementSetting.includes('desktop'))) {
				totalOffset += stickyAnnouncement.getBoundingClientRect().height;
			}
		}

		// Check for sticky header
		const stickyHeader = document.querySelector('.header[data-enable-sticky-header="true"]');
		if (stickyHeader) {
			totalOffset += stickyHeader.getBoundingClientRect().height;
		}

		// Check for sticky filter bar (collection pages)
		const stickyFilterBar = document.querySelector('.filter-bar--sticky');
		if (stickyFilterBar) {
			totalOffset += stickyFilterBar.getBoundingClientRect().height;
		}

		// Calculate the target scroll position
		const elementPosition = readMoreSection.getBoundingClientRect().top + window.scrollY;
		const targetPosition = elementPosition - totalOffset;

		// Smooth scroll to the target position
		window.scrollTo({
			top: targetPosition,
			behavior: 'smooth'
		});
	});
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initCollectionReadMoreScroll);
} else {
	initCollectionReadMoreScroll();
}

// Also initialize on Shopify section load (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
	document.addEventListener('shopify:section:load', initCollectionReadMoreScroll);
}
